import { clearingApi, stationOpsApi } from "../../config/apiConfig";

export const SyncCDR = async () => {
  try {
    const response = await clearingApi.post(`cdrs/sync`);
    if (response?.status >= 300) {
      return false;
    }
    return true;
  } catch (error) {
    console.error(error);
    return false;
  }
};

export const getTransactions = async (id, year, month) => {
  try {
    const response = await clearingApi.get(
      `/cdrs/?year=${year}&month=${month}&station_id=${id} `
    );
    return response?.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

/**
 * Fetch one cursor-paginated page of transactions.
 *
 * Backing endpoint: GET /clearing/transactions (cursor pagination, max 100/page).
 * All params are optional; the backend defaults to the last month, charge_type=all, order=desc.
 * Pages are full: exactly `limit` items unless `nextCursor` is null (CLR-278).
 *
 * A CDR lookup (`cdrId`) makes the backend ignore range, type, user and cursor, so only
 * `cdr_id` (and `limit`) is sent; it returns 0–1 items and `nextCursor: null`.
 *
 * @param {Object}  [params]
 * @param {string}  [params.dateFrom]   Inclusive lower bound, "YYYY-MM-DD".
 * @param {string}  [params.dateTo]     Inclusive upper bound, "YYYY-MM-DD".
 * @param {"all"|"paid"|"free"} [params.chargeType]  Free/paid filter (default "all").
 * @param {string}  [params.userId]     Exact user ID; combines with range and type.
 * @param {string}  [params.cdrId]      Exact CDR ID lookup; overrides every other filter.
 * @param {"desc"|"asc"} [params.order] Time order (default "desc"). A cursor is only valid
 *                                      with the order it was issued for (400 otherwise).
 * @param {string}  [params.cursor]     Opaque cursor from a previous nextCursor.
 * @param {number}  [params.limit]      Page size 1–100 (clamped server-side).
 * @returns {Promise<{ items: Object[], nextCursor: string|null }>}
 *          `nextCursor === null` means this was the last page.
 * @throws  Propagates the axios error so callers can surface a retryable state.
 */
export const getTransactionsPage = async ({
  dateFrom,
  dateTo,
  chargeType,
  userId,
  cdrId,
  order,
  cursor,
  limit,
} = {}) => {
  const params = {};
  if (cdrId) {
    params.cdr_id = cdrId;
  } else {
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (chargeType) params.charge_type = chargeType;
    if (userId) params.user_id = userId;
    if (order) params.order = order;
    if (cursor) params.cursor = cursor;
  }
  if (limit) params.limit = limit;

  const response = await clearingApi.get(`/transactions`, { params });
  const data = response?.data ?? {};

  return {
    items: Array.isArray(data.items) ? data.items : [],
    nextCursor: data.nextCursor ?? null,
  };
};

/**
 * Count the transactions matching a range, charge type and optional user (CLR-278).
 *
 * Backing endpoint: GET /clearing/transactions/count → `{ total: number }`.
 *
 * @param {Object}  [params]
 * @param {string}  [params.dateFrom]   Inclusive lower bound, "YYYY-MM-DD".
 * @param {string}  [params.dateTo]     Inclusive upper bound, "YYYY-MM-DD".
 * @param {"all"|"paid"|"free"} [params.chargeType]
 * @param {string}  [params.userId]     Exact user ID.
 * @returns {Promise<number>} The total.
 * @throws  On a failed request or a response without a numeric `total`, so the caller can
 *          hide the total.
 */
export const getTransactionsCount = async ({ dateFrom, dateTo, chargeType, userId } = {}) => {
  const params = {};
  if (dateFrom) params.date_from = dateFrom;
  if (dateTo) params.date_to = dateTo;
  if (chargeType) params.charge_type = chargeType;
  if (userId) params.user_id = userId;

  const response = await clearingApi.get(`/transactions/count`, { params });
  const total = response?.data?.total;
  if (typeof total !== "number" || !Number.isFinite(total) || total < 0) {
    throw new Error("Transactions count response has no numeric total.");
  }
  return total;
};

/**
 * Fetch all CDRs for a given station and calendar month from the stationOps API.
 * Handles offset pagination automatically (page 0, loops while more pages exist).
 *
 * `to` is always capped to the current instant so we never pass a future timestamp
 * (the endpoint returns 400 if `to` is not in the past).
 *
 * @param {string} locationId  Station / location ID
 * @param {number} year
 * @param {number} month       1–12
 * @returns {Promise<Object[]>}
 */
export const getCdrs = async (locationId, year, month) => {
  const from = new Date(Date.UTC(year, month - 1, 1));
  const nextMonth = new Date(Date.UTC(year, month, 1));
  const now = Date.now();

  // If the entire month hasn't started yet there are no CDRs to fetch.
  if (from.getTime() >= now) return [];

  // Cap `to` to one millisecond before now so it is always a past instant.
  const to = new Date(Math.min(nextMonth.getTime(), now - 1));

  const allItems = [];
  let page = 0;

  for (;;) {
    const response = await stationOpsApi.get("/internal/cdrs", {
      params: {
        from: from.toISOString(),
        to: to.toISOString(),
        location_id: locationId,
        page,
        size: 200,
      },
    });
    const data = response.data ?? {};
    const content = Array.isArray(data.content) ? data.content : [];
    allItems.push(...content);

    const totalPages = data.page?.total_pages ?? 1;
    if (page + 1 >= totalPages) break;
    page += 1;
  }

  return allItems;
};
